import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { CarePolicyNoteT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { toast } from 'sonner';

import { fetchCareHydratePatients, useCareSuggestPatientMutation } from '@/entities/care-patients';
import type { PatientGateIssue } from './types';

export interface PatientEligibilityWarningModel {
  open: boolean;
  isLoading: boolean;
  isSuggesting: boolean;
  issues: PatientGateIssue[];
  onOpenChange: (nextOpen: boolean) => void;
  onContinue: () => void;
  onSuggestNext: () => Promise<void>;
}

function hasPracticeAreaMismatch(notes: CarePolicyNoteT[]): boolean {
  return notes.some((note) => {
    const title = note.title.toLowerCase();
    const detail = note.detail.toLowerCase();
    return title.includes('practice area') || detail.includes('practice area');
  });
}

export function usePatientEligibilityWarning(userId: string): PatientEligibilityWarningModel {
  const navigate = useNavigate();
  const suggestPatientMutation = useCareSuggestPatientMutation();
  const hydrateQuery = useQuery({
    queryKey: ['care', 'patients', 'eligibility', userId],
    enabled: userId.trim() !== '',
    queryFn: async () => fetchCareHydratePatients({ patient_ids: [userId] }),
  });

  const hydratedPatient = hydrateQuery.data?.patients[userId];
  const policyResult = hydratedPatient?.policy_result;
  const notes = policyResult?.notes ?? [];
  const relationship = policyResult?.relationship ?? null;
  const hasKnownRelationship = relationship === 'actionable' || relationship === 'licensed';
  const hasEligibilityWarning =
    relationship === 'unlicensed' || (hasKnownRelationship && hasPracticeAreaMismatch(notes));
  const hasActiveViewerWarning = (hydratedPatient?.viewer_count ?? 0) > 0;

  const issues: PatientGateIssue[] = [];
  if (relationship === 'unlicensed') {
    issues.push({
      code: 'unlicensed',
      title: 'You are not licensed for this patient',
      detail: 'You can continue to review this chart, or move to the next actionable patient.',
    });
  } else if (hasKnownRelationship && hasPracticeAreaMismatch(notes)) {
    issues.push({
      code: 'practice_area_mismatch',
      title: 'This patient may be outside your practice area',
      detail: 'You can continue to review this chart, or move to the next actionable patient.',
    });
  }

  if (hasEligibilityWarning) {
    notes.forEach((note) => {
      issues.push({
        code: 'eligibility_note',
        title: note.title,
        detail: note.detail,
      });
    });
  }

  if (hasActiveViewerWarning) {
    issues.push({
      code: 'active_viewer',
      title: 'Another clinician is currently viewing this patient',
      detail: 'This chart may already be in progress by another clinician.',
    });
  }

  const [trackedPatient, setTrackedPatient] = useState(userId);
  const [dismissed, setDismissed] = useState(false);

  if (trackedPatient !== userId) {
    setTrackedPatient(userId);
    setDismissed(false);
  }

  const open = !hydrateQuery.isLoading && !hydrateQuery.isError && issues.length > 0 && !dismissed;

  const onContinue = () => {
    setDismissed(true);
  };

  const onOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onContinue();
    }
  };

  const onSuggestNext = async () => {
    if (suggestPatientMutation.isPending) {
      return;
    }

    const response = await suggestPatientMutation.mutateAsync();
    const suggestedPatient = response.patient;

    if (suggestedPatient === null) {
      toast.info('No actionable patients are available right now.');
      return;
    }

    setDismissed(true);
    navigate(`/patient/${suggestedPatient.user_id}`);
  };

  return {
    open,
    isLoading: hydrateQuery.isLoading,
    isSuggesting: suggestPatientMutation.isPending,
    issues,
    onOpenChange,
    onContinue,
    onSuggestNext,
  };
}
